/* eslint-disable */
import React, { useEffect, useState } from "react";
import propTypes from "prop-types";
import { Alert, Box, Grid, Typography } from "@mui/material";
import CancelButton from "components/common/CancelButton";
import AcceptButton from "components/common/AcceptButton";
import AppNumberInput from "components/common/AppNumberInput";
import LocalMachineCounterConfigService from "services/localMachineCounterConfigService";
import InitialValuesMachineSummary from "forms/localMachine/InitialValuesMachineSummary";

function CounterInitialValuesForm({
  status,
  localMachine,
  counterConfig,
  closeForm,
  onSuccess,
  onSavingChange,
}) {
  const [form, setForm] = useState({
    InitialCounterIn:
      status?.InitialCounterIn === null || status?.InitialCounterIn === undefined
        ? ""
        : String(status.InitialCounterIn),
    InitialCounterOut:
      status?.InitialCounterOut === null || status?.InitialCounterOut === undefined
        ? ""
        : String(status.InitialCounterOut),
  });
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [loadedCounterConfig, setLoadedCounterConfig] = useState(null);
  const [configError, setConfigError] = useState("");

  const mode = String(status?.Mode || "").toLowerCase();
  const isLocked = mode === "locked" || status?.HasCompletedCollection === true;
  const activeCounterConfig = counterConfig || loadedCounterConfig;

  useEffect(() => {
    if (counterConfig) return undefined;
    const localMachineId = Number(localMachine?.Id ?? localMachine?.LocalMachineId);
    if (!Number.isInteger(localMachineId) || localMachineId <= 0) return undefined;

    let active = true;
    LocalMachineCounterConfigService.getActiveLocalMachineCounterConfig(localMachineId)
      .then((config) => {
        if (active) {
          setLoadedCounterConfig(config || null);
          if (!config) setConfigError("No se encontró la configuración activa de contadores.");
        }
      })
      .catch(() => {
        if (active) setConfigError("No se pudo cargar la fórmula y tolerancia de los contadores.");
      });
    return () => { active = false; };
  }, [counterConfig, localMachine?.Id, localMachine?.LocalMachineId]);

  const validate = () => {
    if (isLocked) return "Ya existe una recaudación para esta instalación.";
    if (!status?.LocalMachineCounterConfigId) return "No se encontró configuración activa de contadores.";
    return "";
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    const nextFieldErrors = {};
    if (form.InitialCounterIn === "") nextFieldErrors.InitialCounterIn = "Ingresá el valor de entrada.";
    else if (!Number.isInteger(Number(form.InitialCounterIn)) || Number(form.InitialCounterIn) < 0) nextFieldErrors.InitialCounterIn = "Ingresá un número entero mayor o igual a cero.";
    if (form.InitialCounterOut === "") nextFieldErrors.InitialCounterOut = "Ingresá el valor de salida.";
    else if (!Number.isInteger(Number(form.InitialCounterOut)) || Number(form.InitialCounterOut) < 0) nextFieldErrors.InitialCounterOut = "Ingresá un número entero mayor o igual a cero.";
    setFieldErrors(nextFieldErrors);
    if (Object.keys(nextFieldErrors).length) {
      setError("");
      return;
    }
    setIsSaving(true);
    onSavingChange?.(true);
    setError("");
    try {
      await LocalMachineCounterConfigService.updateCounterInitialValues(
        status.LocalMachineCounterConfigId,
        {
          InitialCounterIn: Number(form.InitialCounterIn),
          InitialCounterOut: Number(form.InitialCounterOut),
        }
      );
      await onSuccess?.();
    } catch (err) {
      const data = err?.response?.data || {};
      setError(data.error || data.message || "No se pudieron guardar los valores iniciales de contadores.");
    } finally {
      setIsSaving(false);
      onSavingChange?.(false);
    }
  };

  return (
    <Box sx={{ pt: 0.5, minWidth: 0 }}>
      <Box sx={{ mb: 2 }}>
        <InitialValuesMachineSummary
          localMachine={localMachine}
          counterConfig={activeCounterConfig}
        />
      </Box>
      {!activeCounterConfig && (
        <Typography variant="body2" color={configError ? "error" : "text.secondary"} sx={{ mb: 2 }}>
          {configError || "Cargando fórmula y tolerancia de contadores..."}
        </Typography>
      )}
      {isLocked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Estos valores ya no se pueden editar porque existe una recaudación.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <AppNumberInput
            fullWidth
            label="Entrada"
            required
            value={form.InitialCounterIn}
            min={0}
            decimalScale={0}
            onValueChange={(cleanValue) => {
              setForm((prev) => ({ ...prev, InitialCounterIn: cleanValue }));
              setFieldErrors((prev) => ({ ...prev, InitialCounterIn: "" }));
              setError("");
            }}
            inputProps={{ step: 1 }}
            disabled={isLocked || isSaving}
            error={Boolean(fieldErrors.InitialCounterIn)}
            helperText={fieldErrors.InitialCounterIn}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AppNumberInput
            fullWidth
            label="Salida"
            required
            value={form.InitialCounterOut}
            min={0}
            decimalScale={0}
            onValueChange={(cleanValue) => {
              setForm((prev) => ({ ...prev, InitialCounterOut: cleanValue }));
              setFieldErrors((prev) => ({ ...prev, InitialCounterOut: "" }));
              setError("");
            }}
            inputProps={{ step: 1 }}
            disabled={isLocked || isSaving}
            error={Boolean(fieldErrors.InitialCounterOut)}
            helperText={fieldErrors.InitialCounterOut}
          />
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <CancelButton onClick={closeForm} disabled={isSaving} fullWidth>Cancelar</CancelButton>
        </Grid>
        <Grid item xs={12} md={6}>
          <AcceptButton onClick={handleSave} disabled={isLocked || isSaving} fullWidth>
            {isSaving ? "Guardando..." : "Guardar valores iniciales"}
          </AcceptButton>
        </Grid>
      </Grid>
    </Box>
  );
}

CounterInitialValuesForm.propTypes = {
  status: propTypes.object.isRequired,
  localMachine: propTypes.object.isRequired,
  counterConfig: propTypes.object,
  closeForm: propTypes.func.isRequired,
  onSuccess: propTypes.func,
  onSavingChange: propTypes.func,
};

export default CounterInitialValuesForm;
